import React, { useState, lazy, Suspense } from "react";
import { FlatList, Pressable, View, ActivityIndicator } from "react-native";
import styled, { useTheme } from "styled-components/native";
import { VDBText } from "@components/atoms";
import CollapsibleView from "@components/molecules/CollapsibleView";
const PDPModal = lazy(
  () => import("@scenes/jewelry-builder/components/PdpModal")
);
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useProductPrefix } from "../../../hooks/useProductPrefix";

interface ComponentItemProps {
  id: number;
  stock_num: string;
  short_title: string;
  long_title?: string;
  total_sales_price: number;
  image_url?: string;
  image_thumb_url?: string;
  media?: any[];
  size?: string;
  shape?: string;
  color?: string;
  clarity?: string;
  type?: string;
}

interface ComponentsSectionProps {
  components: ComponentItemProps[];
}

const ComponentsSection: React.FC<ComponentsSectionProps> = ({
  components,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [pressedItem, setPressedItem] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] =
    useState<ComponentItemProps | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const productPrefix = useProductPrefix(selectedComponent?.type);
  const handleComponentPress = (component: ComponentItemProps) => {
    setSelectedComponent(component);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedComponent(null);
  };

  const getProductType = (component: ComponentItemProps) => {
    const productType = component.type || "diamond";
    return productType;
  };

  const getDisplayData = () => {
    if (components.length <= 10 || isExpanded) {
      return components;
    }

    const initialItems = components.slice(0, 10);
    const showMoreItem = {
      id: -1,
      stock_num: "show_more",
      short_title: t("global.showMore", "Show More"),
      total_sales_price: 0,
      isShowMore: true,
    };

    return [...initialItems, showMoreItem as any];
  };

  const renderComponent = ({
    item,
    index,
  }: {
    item: ComponentItemProps & { isShowMore?: boolean };
    index: number;
  }) => {
    const displayData = getDisplayData();
    const isLastItem = index === displayData.length - 1;
    const isShowMore = item.isShowMore === true;

    if (isShowMore) {
      return (
        <ComponentItemContainer
          isPressed={pressedItem === item.id}
          onPressIn={() => setPressedItem(item.id)}
          onPressOut={() => setPressedItem(null)}
          onPress={() => setIsExpanded(true)}
          style={{ borderBottomWidth: 0 }}
        >
          <ComponentDetailsContainer>
            <VDBText
              type={typography["Body-M"]}
              color={theme["core-05"]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {item.short_title}
            </VDBText>
          </ComponentDetailsContainer>
        </ComponentItemContainer>
      );
    }

    return (
      <ComponentItemContainer
        isPressed={pressedItem === item.id}
        onPressIn={() => setPressedItem(item.id)}
        onPressOut={() => setPressedItem(null)}
        onPress={() => handleComponentPress(item)}
        style={{ borderBottomWidth: isLastItem ? 0 : 1 }}
      >
        <ComponentDetailsContainer>
          <VDBText
            type={typography["Body-M"]}
            color={theme["core-05"]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {item.short_title || item.long_title || "Component"}
          </VDBText>
        </ComponentDetailsContainer>

        <ArrowContainer>
          <VDBIcon
            name={VDBIcons.ARROW_RIGHT}
            size={24}
            variant={IconsVariant.VDBCustom}
            color={theme["core-04"]}
          />
        </ArrowContainer>
      </ComponentItemContainer>
    );
  };

  if (!components || components.length === 0) {
    return null;
  }

  return (
    <>
      <Container>
        <CollapsibleView
          initExpanded={true}
          title={t("productDetailPage.stones.title")}
          arrowStyling={{
            color: theme["core-04"],
          }}
          applyShadow={false}
        >
          <Divider />
          <ListContainer>
            {components.length > 0 ? (
              <FlatList
                data={getDisplayData()}
                renderItem={renderComponent}
                keyExtractor={(item) => `component-${item.id}`}
                scrollEnabled={false}
              />
            ) : (
              <EmptyContainer>
                <VDBText style={typography["Body-R"]} color={theme["core-04"]}>
                  {t("productDetailPage.stones.empty", "No stones available")}
                </VDBText>
              </EmptyContainer>
            )}
          </ListContainer>
        </CollapsibleView>
      </Container>

      {selectedComponent && (
        <Suspense fallback={<ActivityIndicator />}>
          <PDPModal
            route={{
              params: {
                productID: selectedComponent.id,
                productType: getProductType(selectedComponent),
                fromComponent: true,
                componentData: selectedComponent,
                stoneItemDetail: selectedComponent,
                productPrefix: productPrefix,
              },
            }}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        </Suspense>
      )}
    </>
  );
};

const Container = styled(View)`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 12px;
  margin-vertical: ${SpaceType.Basic}px;
`;

const ComponentItemContainer = styled(Pressable)<{ isPressed: boolean }>`
  flex-direction: row;
  padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
  background-color: ${({ theme, isPressed }) =>
    isPressed ? theme["core-02"] : theme["core-01"]};
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme["core-02"]};
  align-items: center;
`;

const ComponentDetailsContainer = styled(View)`
  flex: 1;
  justify-content: center;
`;

const ArrowContainer = styled(View)`
  padding-left: ${SpaceType.Basic}px;
`;

const EmptyContainer = styled(View)`
  padding: ${SpaceType.LargeDouble}px;
  align-items: center;
`;

const ListContainer = styled(View)`
  padding-vertical: ${SpaceType.ExtraSmall}px;
`;

const Divider = styled(View)`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
`;

export default ComponentsSection;
